import { useCallback, useReducer } from 'react';

type UpdateAction<State> = {
  type: 'update';
  payload: Partial<State>;
};

type ResetAction<State> = {
  type: 'reset';
  payload: State;
};

type WithTouchedFlag<State> = State & { touched: boolean };

export default function useResettableState<State>(initialState: State): [
  State extends null ? WithTouchedFlag<State> | null : WithTouchedFlag<State>,
  {
    update: (payload: Partial<State>) => void;
    reset: (payload: State) => void;
  },
] {
  const [state, dispatch] = useReducer(
    (
      currentState: WithTouchedFlag<State> | null,
      action: UpdateAction<State> | ResetAction<State>,
    ): WithTouchedFlag<State> | null => {
      if (action.type === 'reset') {
        return { ...action.payload, touched: false };
      }

      if (currentState === null) {
        return null;
      }

      return {
        ...currentState,
        ...action.payload,
        touched: true,
      };
    },
    initialState ? { ...initialState, touched: false } : null,
  );

  const update = useCallback((payload: Partial<State>) => {
    dispatch({ type: 'update', payload });
  }, []);

  const reset = useCallback((payload: State) => {
    dispatch({ type: 'reset', payload });
  }, []);

  return [
    state as State extends null
      ? WithTouchedFlag<State> | null
      : WithTouchedFlag<State>,
    { update, reset },
  ];
}
